import React, { useEffect, useMemo, useState } from "react";
import { Activity } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickSport = (trainingData) => {
  const bySport =
    trainingData?.bySport && typeof trainingData.bySport === "object"
      ? trainingData.bySport
      : {};

  const sports = Object.keys(bySport);
  if (sports.length === 0) return { sport: "", fields: {} };

  const preferredSport =
    trainingData?.lastSport && bySport[trainingData.lastSport]
      ? trainingData.lastSport
      : sports[0];

  return {
    sport: preferredSport,
    fields: bySport[preferredSport]?.fields || {},
  };
};

const ExerciseTrendWidget = ({ data }) => {
  const { sport, fields } = useMemo(() => pickSport(data), [data]);
  const fieldEntries = useMemo(() => Object.entries(fields), [fields]);
  const [selectedFieldKey, setSelectedFieldKey] = useState("");

  useEffect(() => {
    if (!fieldEntries.length) {
      setSelectedFieldKey("");
      return;
    }

    if (!fieldEntries.some(([fieldKey]) => fieldKey === selectedFieldKey)) {
      setSelectedFieldKey(fieldEntries[0][0]);
    }
  }, [fieldEntries, selectedFieldKey]);

  const chartPayload = useMemo(() => {
    if (!sport || fieldEntries.length === 0) {
      return { sport, fieldTitle: "", unit: "", chartData: [], latestValue: null };
    }

    const [fieldKey, field] =
      fieldEntries.find(([candidate]) => candidate === selectedFieldKey) ||
      fieldEntries[0];

    const history = Array.isArray(field?.history) ? field.history : [];
    const chartData = history
      .map((item, index) => ({
        value: toNumber(item?.value),
        label: item?.recordedAt
          ? new Date(item.recordedAt).toLocaleDateString("es-CO", {
              day: "2-digit",
              month: "2-digit",
            })
          : `M${index + 1}`,
      }))
      .filter((item) => item.value !== null)
      .slice(-8);

    return {
      sport,
      fieldKey,
      fieldTitle: field?.title || fieldKey,
      unit: field?.unit || "",
      chartData,
      latestValue: field?.latest?.value ?? null,
    };
  }, [fieldEntries, selectedFieldKey, sport]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-full bg-gray-50">
          <Activity className="text-blue-600" size={20} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Evolución Ejercicios</h3>
          <p className="text-xs text-gray-500">
            {chartPayload.sport || "Sin deporte"}
          </p>
        </div>
      </div>

      {fieldEntries.length > 1 ? (
        <div className="mb-4">
          <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.15em] text-gray-500">
            Ejercicio
          </label>
          <select
            value={selectedFieldKey}
            onChange={(event) => setSelectedFieldKey(event.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition-colors focus:border-blue-500"
          >
            {fieldEntries.map(([fieldKey, field]) => (
              <option key={fieldKey} value={fieldKey}>
                {field?.title || fieldKey}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {chartPayload.chartData.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          Sin historial numérico de ejercicios.
        </p>
      ) : (
        <>
          <div className="mb-3">
            <p className="text-sm text-gray-600">{chartPayload.fieldTitle}</p>
            <p className="text-lg font-semibold text-gray-900">
              {chartPayload.latestValue}
              {chartPayload.unit ? (
                <span className="ml-1 text-sm font-medium text-gray-500">
                  {chartPayload.unit}
                </span>
              ) : null}
            </p>
          </div>
          <div className="h-24 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartPayload.chartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f0f0"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  stroke="#9ca3af"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  dy={5}
                />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "none",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#2563eb"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{
                    r: 4,
                    fill: "#fff",
                    stroke: "#2563eb",
                    strokeWidth: 2,
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default ExerciseTrendWidget;
